import { useEffect, useRef, useState } from "react";
import Dialog from "@mui/material/Dialog";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Divider from "@mui/material/Divider";
import CircularProgress from "@mui/material/CircularProgress";
import type { Artwork, ArtStatus, Collection } from "../../types";
import { artImage, fileToDataUrl } from "../../utils/image";
import { tokens } from "../../theme";

const CloseIcon = () => <Box component="span">×</Box>;
const UploadIcon = () => <Box component="span">↑</Box>;

type Draft = Omit<Artwork, "id" | "createdAt">;

const EMPTY: Draft = {
  title: "",
  year: "",
  medium: "",
  dimensions: "",
  galleryAttribution: "",
  description: "",
  collectionId: null,
  status: "available",
  price: "",
  featured: false,
};

export default function ArtworkFormDialog({
  open,
  initial,
  collections,
  onClose,
  onSave,
}: {
  open: boolean;
  initial?: Artwork | null;
  collections: Collection[];
  onClose: () => void;
  onSave: (draft: Draft) => void;
}) {
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [busy, setBusy] = useState(false);
  const [touched, setTouched] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setTouched(false);
      if (initial) {
        const { id: _id, createdAt: _c, ...rest } = initial;
        setDraft(rest);
      } else {
        setDraft(EMPTY);
      }
    }
  }, [open, initial]);

  const set = <K extends keyof Draft>(k: K, v: Draft[K]) =>
    setDraft((d) => ({ ...d, [k]: v }));

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const url = await fileToDataUrl(file);
      setDraft((d) => ({ ...d, image: url }));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const preview = artImage(draft);
  const titleValid = draft.title.trim().length > 0;
  const hasImage = !!preview;

  const save = () => {
    setTouched(true);
    if (!titleValid || !hasImage) return;
    onSave({ ...draft, title: draft.title.trim() });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { bgcolor: tokens.bone } }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 3,
          py: 2,
        }}
      >
        <Typography sx={{ fontFamily: tokens.display, fontSize: "1.7rem" }}>
          {initial ? "Edit artwork" : "Add artwork"}
        </Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider />

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "300px 1fr" },
          gap: 3,
          p: 3,
        }}
      >
        {/* Image */}
        <Box>
          <Box
            sx={{
              aspectRatio: "4 / 5",
              bgcolor: tokens.paper,
              border: `1px solid ${touched && !hasImage ? "#c0392b" : tokens.line}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden",
            }}
          >
            {busy ? (
              <CircularProgress size={26} sx={{ color: tokens.accent }} />
            ) : preview ? (
              <Box
                component="img"
                src={preview}
                alt="Preview"
                sx={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            ) : (
              <Typography
                sx={{
                  color: tokens.taupe,
                  fontSize: "0.85rem",
                  px: 2,
                  textAlign: "center",
                }}
              >
                No image yet
              </Typography>
            )}
          </Box>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            hidden
            onChange={onFile}
          />
          <Button
            onClick={() => fileRef.current?.click()}
            startIcon={<UploadIcon />}
            variant="outlined"
            fullWidth
            sx={{ mt: 1.5 }}
            disabled={busy}
          >
            {preview ? "Replace photo" : "Upload photo"}
          </Button>
          {touched && !hasImage && (
            <Typography sx={{ color: "#c0392b", fontSize: "0.74rem", mt: 1 }}>
              A photo is required.
            </Typography>
          )}
        </Box>

        {/* Fields */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField
            label="Title"
            value={draft.title}
            onChange={(e) => set("title", e.target.value)}
            error={touched && !titleValid}
            helperText={
              touched && !titleValid ? "Give the piece a title." : " "
            }
            fullWidth
          />
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <TextField
              label="Year"
              value={draft.year ?? ""}
              onChange={(e) => set("year", e.target.value)}
              fullWidth
            />
            <TextField
              label="Dimensions"
              placeholder="16 × 20 in"
              value={draft.dimensions ?? ""}
              onChange={(e) => set("dimensions", e.target.value)}
              fullWidth
            />
          </Box>
          <TextField
            label="Medium"
            placeholder="Oil on canvas"
            value={draft.medium ?? ""}
            onChange={(e) => set("medium", e.target.value)}
            fullWidth
          />
          <TextField
            label="Gallery attribution"
            placeholder="Through PAStA Fine Art Gallery"
            value={draft.galleryAttribution ?? ""}
            onChange={(e) => set("galleryAttribution", e.target.value)}
            fullWidth
          />
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <TextField
              select
              label="Status"
              value={draft.status}
              onChange={(e) => set("status", e.target.value as ArtStatus)}
              fullWidth
            >
              <MenuItem value="available">Available</MenuItem>
              <MenuItem value="sold">Sold</MenuItem>
              <MenuItem value="nfs">Not for sale</MenuItem>
            </TextField>
            <TextField
              label="Price"
              placeholder="$420"
              value={draft.price ?? ""}
              onChange={(e) => set("price", e.target.value)}
              disabled={draft.status !== "available"}
              fullWidth
            />
          </Box>
          <TextField
            select
            label="Collection"
            value={draft.collectionId ?? ""}
            onChange={(e) => set("collectionId", e.target.value || null)}
            fullWidth
          >
            <MenuItem value="">No collection</MenuItem>
            {collections.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Description"
            value={draft.description ?? ""}
            onChange={(e) => set("description", e.target.value)}
            multiline
            minRows={3}
            fullWidth
          />
          <FormControlLabel
            control={
              <Switch
                checked={!!draft.featured}
                onChange={(e) => set("featured", e.target.checked)}
              />
            }
            label={
              <Typography sx={{ fontSize: "0.9rem" }}>
                Feature on the home page
              </Typography>
            }
          />
        </Box>
      </Box>

      <Divider />
      <Box
        sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, p: 2.5 }}
      >
        <Button onClick={onClose} sx={{ color: tokens.taupe }}>
          Cancel
        </Button>
        <Button
          onClick={save}
          variant="contained"
          color="primary"
          disabled={busy}
        >
          {initial ? "Save changes" : "Add to gallery"}
        </Button>
      </Box>
    </Dialog>
  );
}
